import * as React from 'react'
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  BooleanField,
  FunctionField,
  EditButton,
  TabbedShowLayout,
  TopToolbar,
  ListButton,
  useGetList,
  useGetMany,
  useRecordContext,
  Loading
} from 'react-admin'
import {
  Typography,
  Box,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material'
import { Link } from 'react-router-dom'

const ProviderGroupShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
  </TopToolbar>
)

// Renders provider names as clickable chips that link to the provider details page
const ProviderChips = ({ providerIds }) => {
  const { data: providers, isLoading } = useGetMany(
    'providers',
    { ids: providerIds || [] },
    { enabled: !!providerIds && providerIds.length > 0 }
  )

  if (!providerIds || providerIds.length === 0) {
    return <Typography variant="body2" color="text.secondary">No providers assigned</Typography>
  }
  if (isLoading) return <Typography variant="body2">Loading...</Typography>

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {(providers || []).map(provider => (
        <Chip
          key={provider.id}
          label={provider.displayName}
          component={Link}
          to={`/providers/${provider.id}/show`}
          clickable
          size="small"
          variant="outlined"
          sx={{ textDecoration: 'none' }}
        />
      ))}
    </Box>
  )
}

const LocationsTab = () => {
  const record = useRecordContext()
  const { data: locations, isLoading } = useGetList(
    'provider-locations',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: { providerGroupId: record?.id }
    },
    { enabled: !!record }
  )

  if (!record) return null
  if (isLoading) return <Loading />
  if (!locations || locations.length === 0) {
    return <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>No locations found for this group.</Typography>
  }

  return (
    <Box sx={{ p: 2 }}>
      {locations.map((location, idx) => (
        <Box key={location.id}>
          <SimpleShowLayout record={location} spacing={2} className="details-page-layout" sx={{ px: 0 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Location ID</Typography>
              <Typography variant="body2">{location.providerLocationId}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Location Name</Typography>
              <Typography variant="body2">{location.locationName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Created At</Typography>
              <Typography variant="body2">{location.createdAt ? new Date(location.createdAt).toLocaleString() : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Associated Providers</Typography>
              <Box sx={{ mt: 0.5 }}>
                <ProviderChips providerIds={location.providerIds} />
              </Box>
            </Box>
          </SimpleShowLayout>
          {idx < locations.length - 1 && <Divider sx={{ my: 2 }} />}
        </Box>
      ))}
    </Box>
  )
}

const ContractsTab = () => {
  const record = useRecordContext()
  const { data: contracts, isLoading } = useGetList(
    'provider-contracts',
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'startDate', order: 'DESC' },
      filter: { providerGroupId: record?.id }
    },
    { enabled: !!record }
  )

  if (!record) return null
  if (isLoading) return <Loading />
  if (!contracts || contracts.length === 0) {
    return <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>No contracts found for this group.</Typography>
  }

  return (
    <Box sx={{ p: 2 }}>
      {contracts.map((contract, idx) => (
        <Box key={contract.id}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Contract ID</Typography>
              <Typography variant="body2">{contract.providerContractId}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Billing Period</Typography>
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{contract.billingPeriod}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Start Date</Typography>
              <Typography variant="body2">{contract.startDate || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">End Date</Typography>
              <Typography variant="body2">{contract.endDate || 'Ongoing'}</Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            B2B Pricing Tiers
          </Typography>
          {contract.pricingTiers && contract.pricingTiers.length > 0 ? (
            <Table size="small" sx={{ mb: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Min Volume</strong></TableCell>
                  <TableCell><strong>Price / Kit Shipped</strong></TableCell>
                  <TableCell><strong>Price / Kit Tested</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contract.pricingTiers.map((tier, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{tier.minVolume ?? 0}+</TableCell>
                    <TableCell>${Number(tier.pricePerKitShipped).toFixed(2)}</TableCell>
                    <TableCell>${Number(tier.pricePerKitTested).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary">No pricing tiers configured.</Typography>
          )}

          {idx < contracts.length - 1 && <Divider sx={{ my: 3 }} />}
        </Box>
      ))}
    </Box>
  )
}

const ProviderGroupDetails = (props) => {
  return (
    <Show actions={<ProviderGroupShowActions />} {...props}>
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Group Details">
          <SimpleShowLayout spacing={2} className="details-page-layout">
            <TextField source="id" label="Provider Group ID" />
            <TextField source="name" label="Group Name" />
            <TextField source="adminName" label="Admin Name" />
            <TextField source="adminEmail" label="Admin Email" />
            <TextField source="shopifyCompanyName" label="Shopify Company Name" />
            <BooleanField source="splitInvoiceByLocation" label="Split Invoice by Location" />
            {/* Coverage is opt-in, and groups predating this field have no stored
                value — so anything but an explicit true reads as No, matching the
                pre-flight check on the API side. */}
            <FunctionField
              label="Provide Replacement Coupons"
              source="provideReplacementCoupons"
              render={(record) => (record.provideReplacementCoupons === true ? 'Yes' : 'No')}
            />
            <DateField source="createdAt" label="Created At" showTime />
          </SimpleShowLayout>
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Locations">
          <LocationsTab />
        </TabbedShowLayout.Tab>

        <TabbedShowLayout.Tab label="Contracts">
          <ContractsTab />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default ProviderGroupDetails
